import React, { useState, useEffect, useCallback } from 'react';
import { BookmarkIcon } from './Icons';
import { bookmarksAPI } from '../services/api';
import { activateOnKey } from '../utils/a11y';
import styles from '../styles/ContentCard.module.css';

/**
 * Article Card Component
 * Displays educational article cards
 *
 * Props:
 * - id: Article ID (for bookmarking)
 * - title: Article title
 * - slug: Article slug for linking
 * - category: Article category (optional, not in Strapi schema)
 * - excerpt: Optional excerpt/summary
 * - heroImage: Optional hero image URL
 * - onClick: Optional click handler (receives slug)
 * - showBookmark: Show bookmark button (default: true)
 * - isBookmarked: Initial bookmark state (optional, will check API if not provided)
 */
function ArticleCard({ id, title, slug, category, excerpt, heroImage, onClick, showBookmark = true, isBookmarked: initialIsBookmarked, isShared = false }) {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked || false);
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);
  const categoryLabel = category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Article';

  // Bookmarks key content by numeric DB id. An unhydrated content tag can carry
  // a string slug instead — that can't be bookmarked, so resolve to null.
  const numericId = (id != null && id !== '' && Number.isInteger(Number(id)))
    ? Number(id)
    : null;

  const checkBookmarkStatus = useCallback(async () => {
    try {
      const result = await bookmarksAPI.isBookmarked('article', numericId);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[ArticleCard] Failed to check bookmark status:', error);
    }
  }, [numericId]);

  // Check if bookmarked on mount (only if not provided as prop)
  useEffect(() => {
    if (showBookmark && numericId != null && initialIsBookmarked === undefined) {
      checkBookmarkStatus();
    }
  }, [numericId, showBookmark, initialIsBookmarked, checkBookmarkStatus]);

  // Update local state when prop changes
  useEffect(() => {
    if (initialIsBookmarked !== undefined) {
      setIsBookmarked(initialIsBookmarked);
    }
  }, [initialIsBookmarked]);

  const handleBookmarkClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isTogglingBookmark) return;

    setIsTogglingBookmark(true);
    try {
      const result = await bookmarksAPI.toggleBookmark('article', numericId);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[ArticleCard] Failed to toggle bookmark:', error);
    } finally {
      setIsTogglingBookmark(false);
    }
  };

  const handleActivate = () => onClick?.(slug, { title, category });

  // Only expose the card as an operable control when it actually does
  // something — an unclickable card must stay out of the tab order.
  const interactiveProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Open article: ${title}`,
        onClick: handleActivate,
        onKeyDown: activateOnKey(handleActivate)
      }
    : {};

  return (
    <div
      className={styles.contentCard}
      {...interactiveProps}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {heroImage && (
        <div className={styles.contentCardImage}>
          <img src={heroImage} alt={title} className={styles.image} />
          <div className={styles.contentCardBadge}>{categoryLabel}</div>
          {isShared && <div className={styles.sharedChip}>Shared with you before</div>}
          {showBookmark && numericId != null && (
            <button
              className={styles.bookmarkButton}
              onClick={handleBookmarkClick}
              disabled={isTogglingBookmark}
              aria-label={isBookmarked ? `Remove bookmark from ${title}` : `Bookmark ${title}`}
            >
              <BookmarkIcon size={20} filled={isBookmarked} />
            </button>
          )}
        </div>
      )}

      <div className={styles.contentCardContent}>
        {!heroImage && <div className={styles.contentCardBadge}>{categoryLabel}</div>}
        {!heroImage && isShared && <div className={styles.sharedChipInline}>Shared with you before</div>}
        <div className={styles.contentCardTitle}>{title}</div>

        {excerpt && (
          <div className={styles.contentCardDescription}>
            {excerpt}
          </div>
        )}

        <div className={styles.contentCardMeta}>
          <div className={styles.contentCardMetaItem}>
            Read Article
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArticleCard;
